import maInput from '@/components/input'
import maInputText from '@/components/input/modules/text'
import maEventBus from '@/components/eventbus/index.js'

/**
 * @module
 * @name InputSelect
 * @extends Input
 */
export default {
  name: 'InputSelect',
  extends: maInput,
  components: { maInputText },
  /**
   * @function
   * @property {Boolean} focus
   * @property {Boolean} dropdownUpward
   * @property {String} optionFocused
   * @property {*} optionNull
   */
  data: function () {
    return {
      focus: false,
      dropdownUpward: false,
      optionFocused: '',
      optionNull: null
    }
  },

  /**
   * @property {String} name Native HTML element name.
   * @property {String} icon VueSVGicon name.
   * @property {Array} options Array of options. Options should contains at least
   *                           key and value.
   * @property {Boolean} nullValue If true, a null selection are added to computed
   *                               options, used to unselect any option.
   */
  props: {
    name: {
      type: String,
      required: true
    },
    icon: {
      type: String,
      required: false
    },
    options: {
      type: Array,
      required: true
    },
    nullValue: {
      type: Boolean,
      required: false
    }
  },

  /**
   * @property {Array} comOptions Array of options. If nullValue, a null option
   *                              is added.
   * @property {String} selectedOption Selected option key
   * @property {String} selectedLiteral Selected option literal
   */
  computed: {
    comOptions: function () {
      if (this.options && this.options.length > 0 &&
          this.nullValue && !this.optionNull) {
        const optionNull = { key: null, literal: this.$t('common.noSelected') }
        return [optionNull, ...this.options]
      } else {
        return this.options
      }
    },
    selectedOption: function () {
      return this.value ? this.comOptions.find(option => option.key === this.value) : null
    },
    selectedLiteral: function () {
      if (this.comOptions.length <= 0) {
        return this.$t('common.noOptions')
      } else {
        return this.selectedOption ? this.selectedOption.literal : this.$t('common.noSelected')
      }
    }
  },

  methods: {
    triggerFocus() {
      this.$el.querySelector('.input--select .input__el').focus()
    },
    /**
     * @function
     * @name setDefaultValue
     * @description Set default value if exists. This function finds it in order
     *              to get key, value tupla in existing options. If there isn't
     *              any value, first value is selected.
     */
    setDefaultValue: function () {
      if (this.comOptions && this.comOptions.length > 0) {
        if (this.value) {
          const selectedOption = this.comOptions.filter(option => option.key === this.value)
          if (selectedOption.length > 0) {
            this.comOptionselected = selectedOption[0]
            this.optionFocused = selectedOption[0]
          } else {
            this.selectFirstOption()
          }
        } else {
          this.selectFirstOption()
        }
      } else {
        this.comOptionselected = null
      }
    },

    /**
     * @function
     * @name ableKeyNav
     */
    ableKeyNav () {
      this.optionFocused = this.comOptionselected

      if (this.focus === false) {
        this.handleDropdown()
      }
    },

    /**
     * @function
     * @name selectFirstOption
     * @description Set first option and focus it.
     */
    selectFirstOption () {
      this.comOptionselected = this.comOptions[0]
      this.optionFocused = this.comOptions[0]
    },

    /**
     * @function
     * @name handleFocus
     */
    handleFocus: function () {
      this.focus = !this.focus
    },

    /**
     * @function
     * @name isFocus
     * @param {String} optionKey Option key
     * @description Method that returns if option passed as param is the same as
     *              focused.
     */
    isFocus: function (optionKey) {
      return optionKey === this.optionFocused.key
    },

    /**
     * @function
     * @param {Object} event
     * @description Navigates dropdown selecting above option.
     */
    keyNavUp (event) {
      event.preventDefault()
      let optionSelectedIndx = this.comOptions.findIndex(option => option.key === this.optionFocused.key)
      if (optionSelectedIndx > 0) {
        this.optionFocused = this.comOptions[optionSelectedIndx - 1]
      }
    },

    /**
     * @function
     * @param {Object} event
     * @description Navigates dropdown selecting below option.
     */
    keyNavDown (event) {
      event.preventDefault()
      let optionSelectedIndx = this.comOptions.findIndex(option => option.key === this.optionFocused.key)

      if (optionSelectedIndx < this.comOptions.length - 1) {
        this.optionFocused = this.comOptions[optionSelectedIndx + 1]
      }
    },

    /**
     * @function
     * @description Select value on enter and blur input.
     */
    updateValueOnEnter () {
      this.$el.querySelector('input').blur()
      this.selectOption(this.optionFocused)
    },

    /**
     * @function
     * @param {Object} option.key Option key.
     * @param {Object} option.value Option value.
     * @description Select param option as selected.
     */
    selectOption (option) {
      this.showOptions = false
      this.comOptionselected = option
      this.$emit('change', option.key)
    },

    /**
     * @function
     * @name handleDropdown
     */
    handleDropdown: function () {
      const inputDomElement = this.$el
      const dropdownDomElement = inputDomElement.querySelector('.input--select__dropdown')
      const dropdownOptionsDomElement = inputDomElement.querySelector('.input--select__dropdown li')

      if (!this.focus) {
        dropdownDomElement.style.maxHeight = '0px'
        return
      }

      const navbarHeight = 50
      const dropdownOptionHeight = dropdownOptionsDomElement ? dropdownOptionsDomElement.offsetHeight : 10
      const maxOptionNumber = 4
      const maxDropdownHeightValue = dropdownOptionHeight * maxOptionNumber
      const dropdownOffsetMargin = 18

      const windowHeight = window.innerHeight

      const inputDomElementHeight = inputDomElement.offsetHeight

      const inputTopOffset = inputDomElement.getBoundingClientRect().top
      const inputBottomOffset = windowHeight - (inputTopOffset + inputDomElementHeight)

      if (inputTopOffset > inputBottomOffset) {
        this.dropdownUpward = true
        const dropdownFitOnOffset = inputTopOffset > (maxDropdownHeightValue + dropdownOffsetMargin + navbarHeight)

        if (dropdownFitOnOffset) {
          dropdownDomElement.style.maxHeight = maxDropdownHeightValue + 'px'
        } else {
          dropdownDomElement.style.maxHeight = (inputTopOffset - (dropdownOffsetMargin + navbarHeight)) + 'px'
        }
      } else {
        this.dropdownUpward = false
        const dropdownFitOnOffset = inputBottomOffset > (maxDropdownHeightValue + dropdownOffsetMargin)

        if (dropdownFitOnOffset) {
          dropdownDomElement.style.maxHeight = maxDropdownHeightValue + 'px'
        } else {
          dropdownDomElement.style.maxHeight = (inputBottomOffset - dropdownOffsetMargin) + 'px'
        }
      }
    }
  },
  /**
   * @property {function} focus Handle dropdown on focus.
   * @property {function} options Watch options. If change and there isn't any
   *                              option selected, set first option.
   */
  watch: {
    focus: function () {
      this.handleDropdown()
    },
    options: function () {
      if (!this.comOptionselected) this.selectFirstOption()
    }
  },

  mounted: function() {
    // Set eventBus listener for hiding dropdown when scrolling
    maEventBus.$on('onScroll', () => {
      if (this.focus) this.$el.querySelector('.input--select .input__el').blur()
    })
  }
}
